/**
 * The two mappings a boolean grid cell needs: value → word, and word → value.
 *
 * Pulled out of `NxTableBoolColumn.vue` so the edit cell can derive its display
 * value from ITS OWN row instead of from a column-wide ref. That ref was the
 * defect: an inline-editable grid renders an edit cell for EVERY row at once,
 * and all of them wrote into the same `ref` — so every row showed the value of
 * whichever row rendered last. Measured on the Steuerfälle grid (I-1476), where
 * eight rows with six different values all read "Ja"; the same column shape is
 * used by the company mask's bank accounts and communication channels, which
 * carried the fault unnoticed because those lists are short and the flag is
 * usually set on one row.
 *
 * Keeping them here rather than inline also makes them testable without
 * mounting a grid — the pattern `numberCell.ts` already follows.
 */

/**
 * The word a cell shows. `null` and `undefined` map to the EMPTY string, never
 * to the false word: a flag nobody has set is not the same statement as a flag
 * explicitly set to no, and the CSV export has said so since this column
 * existed.
 */
export function boolCellDisplay(
  value: boolean | null | undefined,
  trueDisplayValue: string,
  falseDisplayValue: string
): string {
  if (value === true) return trueDisplayValue;
  if (value === false) return falseDisplayValue;
  return '';
}

/**
 * The value a picked word means. The placeholder (`null` from the select) stays
 * `null` rather than collapsing to `false` — clearing a nullable flag and
 * setting it to no are different writes, and only the caller knows whether its
 * column allows the former.
 */
export function boolCellValue(
  word: string | null | undefined,
  trueDisplayValue: string
): boolean | null {
  if (word === null || word === undefined) return null;
  return word === trueDisplayValue;
}

/**
 * How a boolean column shows its value: `select` is the yes/no pick list (the
 * default), `checkbox` a centred `NxCheckbox` in the display and edit cell —
 * for flags a row is switched on and off with (I-3485). The filter row stays a
 * yes/no select either way, and so does the create row: there "not set" still
 * has to be expressible.
 */
export type NxTableBoolColumnVariant = 'select' | 'checkbox';

/**
 * The state a checkbox cell shows. Only `true` is ticked — a flag nobody has
 * set (`null`) reads as unticked, the same as no.
 */
export function boolCellChecked(value: boolean | null | undefined): boolean {
  return value === true;
}
